import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ListChecks, Plus, Trash2, Star, Sparkles, Sun, BookOpen, Home, Moon, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const routineTypes = [
  { value: 'morning', label: 'Morning', icon: Sun, color: 'bg-yellow-100 text-yellow-600' },
  { value: 'school_prep', label: 'School Prep', icon: BookOpen, color: 'bg-blue-100 text-blue-600' },
  { value: 'after_school', label: 'After School', icon: Home, color: 'bg-green-100 text-green-600' },
  { value: 'bedtime', label: 'Bedtime', icon: Moon, color: 'bg-purple-100 text-purple-600' },
  { value: 'weekly', label: 'Weekly', icon: Star, color: 'bg-orange-100 text-orange-600' },
];

const RoutinesPage = () => {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState('all');
  const [aiLoading, setAiLoading] = useState(false);
  
  // HITL State for AI tips
  const [aiTip, setAiTip] = useState(null);
  const [aiTipCallId, setAiTipCallId] = useState(null);
  const [editedSteps, setEditedSteps] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  
  const [newRoutine, setNewRoutine] = useState({
    child_name: '',
    routine_type: 'morning',
    task: '',
    order: 0
  });

  // HITL confirmation helper
  const confirmAIAction = async (callId, userAction, editDelta = []) => {
    if (!callId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: callId,
        user_action: userAction,
        edit_delta: editDelta
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (aiTipCallId && aiTip) {
        confirmAIAction(aiTipCallId, 'ignored');
      }
    };
  }, [aiTipCallId, aiTip]);

  useEffect(() => {
    fetchRoutines();
  }, []);

  const fetchRoutines = async () => {
    try {
      const response = await axios.get(`${API}/routines`);
      setRoutines(response.data);
    } catch (error) {
      console.error('Error fetching routines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/routines`, newRoutine);
      toast.success('Routine item added!');
      setDialogOpen(false);
      setNewRoutine({ child_name: '', routine_type: 'morning', task: '', order: 0 });
      fetchRoutines();
    } catch (error) {
      toast.error('Failed to add routine');
    }
  };

  const toggleComplete = async (routine) => {
    try {
      const newStreak = routine.is_completed ? routine.streak : routine.streak + 1;
      await axios.put(`${API}/routines/${routine.id}`, { 
        ...routine, 
        is_completed: !routine.is_completed,
        streak: newStreak
      });
      fetchRoutines();
    } catch (error) {
      toast.error('Failed to update routine');
    }
  };

  const deleteRoutine = async (id) => {
    try {
      await axios.delete(`${API}/routines/${id}`);
      toast.success('Routine item removed');
      fetchRoutines();
    } catch (error) {
      toast.error('Failed to delete routine');
    }
  };

  const getAiTip = async (routineType) => {
    setAiLoading(true);
    setAiTipCallId(null);
    try {
      const response = await axios.post(`${API}/ai/routine-tip`, {
        routine_type: routineType,
        child_age: 'school-age'
      });
      const suggestion = response.data.suggestion;
      setAiTip({ type: routineType, content: suggestion });
      setAiTipCallId(response.data.call_id || null);
      // Initialize editable steps
      if (suggestion?.steps) {
        setEditedSteps(JSON.parse(JSON.stringify(suggestion.steps)));
      }
      setIsEditing(false);
    } catch (error) {
      toast.error('AI tips unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  // HITL: Update a step field
  const updateStep = (index, field, value) => {
    const updated = [...editedSteps];
    updated[index] = { ...updated[index], [field]: value };
    setEditedSteps(updated);
    setIsEditing(true);
  };

  // HITL: Calculate edit delta
  const calculateEditDelta = () => {
    const delta = [];
    if (!aiTip?.content?.steps) return delta;
    editedSteps.forEach((edited, index) => {
      const original = aiTip.content.steps[index];
      if (!original) return;
      if (edited.task !== original.task) {
        delta.push({
          field: `step_${index}_task`,
          original: String(original.task || ''),
          corrected: String(edited.task || '')
        });
      }
    });
    return delta;
  };

  // HITL: Save all AI routine steps
  const handleUseRoutineSteps = async () => {
    const editDelta = calculateEditDelta();
    const userAction = editDelta.length > 0 ? 'edited' : 'accepted';
    
    await confirmAIAction(aiTipCallId, userAction, editDelta);
    
    // Get child name (use first child or prompt)
    const childName = selectedChild !== 'all' ? selectedChild : (children[0] || 'Child');
    
    // Save each step as a routine
    for (const step of editedSteps) {
      try {
        await axios.post(`${API}/routines`, {
          child_name: childName,
          routine_type: aiTip.type,
          task: step.task,
          order: step.order || 0
        });
      } catch (error) {
        console.error('Failed to save routine step:', error);
      }
    }
    
    toast.success(`${editedSteps.length} routine steps added for ${childName}!`);
    setAiTip(null);
    setAiTipCallId(null);
    setEditedSteps([]);
    setIsEditing(false);
    fetchRoutines();
  };

  // HITL: Discard AI routine tips
  const handleDiscardRoutineTips = async () => {
    await confirmAIAction(aiTipCallId, 'ignored');
    setAiTip(null);
    setAiTipCallId(null);
    setEditedSteps([]);
    setIsEditing(false);
    toast.info('AI routine tips discarded');
  };

  const children = [...new Set(routines.map(r => r.child_name))];
  const filteredRoutines = routines.filter(r => 
    selectedChild === 'all' || r.child_name === selectedChild
  );

  const getRoutinesByType = (type) => {
    return filteredRoutines
      .filter(r => r.routine_type === type)
      .sort((a, b) => a.order - b.order);
  };

  return (
    <div className="space-y-6" data-testid="routines-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <ListChecks className="w-5 h-5 text-indigo-600" />
            </div>
            Routines
          </h1>
          <p className="text-nurture-muted mt-1">Daily routines for each child</p>
        </div>

        <div className="flex items-center gap-3">
          {children.length > 0 && (
            <Select value={selectedChild} onValueChange={setSelectedChild}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="child-filter">
                <SelectValue placeholder="All Children" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Children</SelectItem>
                {children.map(child => (
                  <SelectItem key={child} value={child}>{child}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-routine-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Routine Item
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading">Add Routine Item</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Child Name</Label>
                  <Input
                    value={newRoutine.child_name}
                    onChange={(e) => setNewRoutine({ ...newRoutine, child_name: e.target.value })}
                    placeholder="Emma"
                    required
                    className="rounded-xl"
                    data-testid="routine-child-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Routine Type</Label>
                  <Select value={newRoutine.routine_type} onValueChange={(v) => setNewRoutine({ ...newRoutine, routine_type: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="routine-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {routineTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Task</Label>
                  <Input
                    value={newRoutine.task}
                    onChange={(e) => setNewRoutine({ ...newRoutine, task: e.target.value })}
                    placeholder="Brush teeth"
                    required
                    className="rounded-xl"
                    data-testid="routine-task-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={newRoutine.order}
                    onChange={(e) => setNewRoutine({ ...newRoutine, order: parseInt(e.target.value) || 0 })}
                    placeholder="1"
                    className="rounded-xl"
                    data-testid="routine-order"
                  />
                </div>
                <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="routine-submit">
                  Add Routine Item
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* AI Tip with HITL */}
      {aiTip && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-none rounded-3xl">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold text-nurture-navy">
                      {aiTip.content?.routine_name || `${routineTypes.find(t => t.value === aiTip.type)?.label} Routine`}
                    </h3>
                    <Badge variant="outline" className={isEditing ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                      {isEditing ? 'Edited' : 'AI Generated'}
                    </Badge>
                  </div>
                  
                  {/* Editable Steps */}
                  {editedSteps.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {editedSteps.map((step, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-indigo-200">
                          <span className="text-sm font-medium text-indigo-500 w-6">{step.order || index + 1}.</span>
                          <Input
                            value={step.task || ''}
                            onChange={(e) => updateStep(index, 'task', e.target.value)}
                            placeholder="Task description"
                            className="flex-1"
                            data-testid={`routine-step-${index}`}
                          />
                          {step.duration_mins && (
                            <Badge variant="outline" className="text-xs">
                              {step.duration_mins} min
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Tip */}
                  {aiTip.content?.tip && (
                    <p className="text-sm text-nurture-muted mb-4 italic">
                      💡 {aiTip.content.tip}
                    </p>
                  )}
                  
                  {/* HITL Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-indigo-200">
                    <Button
                      onClick={handleUseRoutineSteps}
                      className="bg-nurture-sage hover:bg-nurture-sage-hover text-white"
                      data-testid="use-routine-steps-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isEditing ? 'Save Edited Steps' : 'Add All Steps'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDiscardRoutineTips}
                      data-testid="discard-routine-tips-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Discard
                    </Button>
                    <span className="text-xs text-nurture-muted ml-auto">
                      {selectedChild !== 'all' ? `For ${selectedChild}` : `For ${children[0] || 'Child'}`}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Routine Tabs */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-48 bg-nurture-cream-dark rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : (
        <Tabs defaultValue="morning" className="space-y-6">
          <TabsList className="bg-nurture-cream-dark rounded-full p-1 flex-wrap h-auto gap-1">
            {routineTypes.map(type => {
              const Icon = type.icon;
              const count = getRoutinesByType(type.value).length;
              return (
                <TabsTrigger
                  key={type.value}
                  value={type.value}
                  className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
                  data-testid={`tab-${type.value}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {type.label}
                  {count > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">{count}</Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {routineTypes.map(type => {
            const typeRoutines = getRoutinesByType(type.value);
            const Icon = type.icon;
            
            return (
              <TabsContent key={type.value} value={type.value}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${type.color} flex items-center justify-center`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <h2 className="text-xl font-heading font-bold text-nurture-navy">{type.label} Routine</h2>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => getAiTip(type.value)}
                    disabled={aiLoading}
                    className="rounded-full"
                    data-testid={`ai-tip-${type.value}`}
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Get Tips
                  </Button>
                </div>

                {typeRoutines.length === 0 ? (
                  <Card className="rounded-3xl shadow-nurture">
                    <CardContent className="py-12 text-center">
                      <Icon className="w-12 h-12 mx-auto text-nurture-muted/30 mb-4" />
                      <p className="text-nurture-muted">No {type.label.toLowerCase()} routine items yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Group by child */}
                    {[...new Set(typeRoutines.map(r => r.child_name))].map(childName => {
                      const childRoutines = typeRoutines.filter(r => r.child_name === childName);
                      const completedCount = childRoutines.filter(r => r.is_completed).length;
                      
                      return (
                        <motion.div
                          key={childName}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <Card className="rounded-3xl shadow-nurture">
                            <CardHeader className="pb-2">
                              <CardTitle className="font-heading text-nurture-navy flex items-center justify-between">
                                {childName}
                                <Badge variant="outline" className={completedCount === childRoutines.length ? 'bg-nurture-sage/10 text-nurture-sage' : ''}>
                                  {completedCount}/{childRoutines.length}
                                </Badge>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                <AnimatePresence>
                                  {childRoutines.map((routine, index) => (
                                    <motion.div
                                      key={routine.id}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: index * 0.05 }}
                                      className={`flex items-center gap-3 p-3 rounded-xl group ${
                                        routine.is_completed ? 'bg-nurture-sage/10' : 'bg-nurture-cream/50'
                                      }`}
                                    >
                                      <span className="text-sm font-medium text-nurture-muted w-6">
                                        {routine.order || index + 1}.
                                      </span>
                                      <Checkbox
                                        checked={routine.is_completed}
                                        onCheckedChange={() => toggleComplete(routine)}
                                        className="w-5 h-5"
                                        data-testid={`routine-checkbox-${routine.id}`}
                                      />
                                      <span className={`flex-1 ${routine.is_completed ? 'line-through text-nurture-muted' : 'text-nurture-navy'}`}>
                                        {routine.task}
                                      </span>
                                      {routine.streak > 0 && (
                                        <Badge variant="outline" className="text-xs">
                                          <Star className="w-3 h-3 mr-1 text-yellow-500" />
                                          {routine.streak}
                                        </Badge>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => deleteRoutine(routine.id)}
                                        className="opacity-0 group-hover:opacity-100 h-7 w-7 text-nurture-muted hover:text-red-500"
                                        data-testid={`delete-routine-${routine.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </motion.div>
                                  ))}
                                </AnimatePresence>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default RoutinesPage;
